package com.evoapps.evoshift.domain.salesimport

import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetException
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetFormat
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetProblem
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetReader
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/** SH-123: the kind of file a sales export came in. */
enum class SalesFileFormat { CSV, XLSX }

/** Why a whole file cannot be imported. */
enum class SalesFileProblem {
    /** Nothing in it (or only blank lines). */
    EMPTY,
    /** Not a CSV text or an .xlsx workbook we can open. */
    UNREADABLE,
    /** Larger than [SalesSheetParser.MAX_BYTES] or longer than [SalesSheetParser.MAX_ROWS] rows. */
    TOO_LARGE,
    /** No row has a date and an amount. */
    NO_SALES
}

class SalesFileException(val problem: SalesFileProblem) : IllegalArgumentException("Sales file: $problem")

/**
 * Which columns hold what (documented in docs/pos-import.md). [headerRow] is the zero-based row
 * with the column names, null when the file has none (then: date, net, gross by position). The
 * names are as the file has them, for the preview.
 */
data class SalesColumnMapping(
    val headerRow: Int?,
    val dateColumn: Int,
    val netColumn: Int,
    val grossColumn: Int? = null,
    val dateHeader: String? = null,
    val netHeader: String? = null,
    val grossHeader: String? = null
)

/** One day's net sales (and gross, when the file has them); several rows of a day are added up. */
data class SalesDay(
    val date: LocalDate,
    val net: Double,
    val gross: Double? = null,
    /** The file's row numbers (1-based, as a spreadsheet shows them) this day comes from. */
    val rows: List<Int> = emptyList()
)

enum class SalesRowProblem { DATE, NET_AMOUNT, GROSS_AMOUNT, NEGATIVE }

/** A row left out of the import: its 1-based number, what is wrong and the cell as written. */
data class SalesRowIssue(val row: Int, val problem: SalesRowProblem, val value: String)

/** A file read for the preview: the mapping found, the days (by date) and the rows left out. */
data class SalesSheet(
    val format: SalesFileFormat,
    val mapping: SalesColumnMapping,
    val days: List<SalesDay>,
    val issues: List<SalesRowIssue>
)

/**
 * SH-123: reads a daily sales export (CSV or XLSX) from a till or a spreadsheet: a date and the
 * day's NET sales per row, optionally the gross. Columns are found by their names (Polish or
 * English, e.g. "Data" / "Netto" / "Brutto", "Date" / "Net sales" / "Gross"), in the first
 * [HEADER_SEARCH_ROWS] rows (exports often start with a title); without names the first three
 * columns are date, net, gross. Dates: 2026-02-01, 01.02.2026, 01/02/2026 (day first) or an
 * Excel date. Amounts: 1 234,56 / 1234.56 / 1.234,56, currency signs ignored. Rows of the
 * same date are added up (one per till); total rows ("Suma", "Razem", "Total") are skipped.
 */
object SalesSheetParser {
    const val MAX_ROWS = SpreadsheetReader.MAX_ROWS
    const val MAX_BYTES = SpreadsheetReader.MAX_FILE_BYTES
    private const val HEADER_SEARCH_ROWS = 10

    /** SH-214: read by the shared [SpreadsheetReader] (the first sheet with something in it). */
    suspend fun parse(bytes: ByteArray): SalesSheet {
        if (bytes.isEmpty()) throw SalesFileException(SalesFileProblem.EMPTY)
        if (bytes.size > MAX_BYTES) throw SalesFileException(SalesFileProblem.TOO_LARGE)
        val spreadsheet = try {
            SpreadsheetReader.read(bytes, MAX_ROWS)
        } catch (e: SpreadsheetException) {
            throw SalesFileException(
                when (e.problem) {
                    SpreadsheetProblem.EMPTY -> SalesFileProblem.EMPTY
                    SpreadsheetProblem.TOO_LARGE, SpreadsheetProblem.TOO_LARGE_UNPACKED -> SalesFileProblem.TOO_LARGE
                    else -> SalesFileProblem.UNREADABLE
                }
            )
        }
        val format = if (spreadsheet.format == SpreadsheetFormat.XLSX) SalesFileFormat.XLSX else SalesFileFormat.CSV
        return parseRows(spreadsheet.sheets[spreadsheet.firstFilledSheet].rows, format)
    }

    fun parseRows(rows: List<List<String>>, format: SalesFileFormat): SalesSheet {
        if (rows.none { row -> row.any { it.isNotBlank() } }) throw SalesFileException(SalesFileProblem.EMPTY)
        if (rows.size > MAX_ROWS) throw SalesFileException(SalesFileProblem.TOO_LARGE)
        val mapping = mappingOf(rows, format)
        val issues = mutableListOf<SalesRowIssue>()
        val byDate = linkedMapOf<LocalDate, SalesDay>()
        val firstData = (mapping.headerRow ?: -1) + 1
        for (index in firstData until rows.size) {
            val row = rows[index]
            val number = index + 1
            val dateCell = row.getOrNull(mapping.dateColumn).orEmpty().trim()
            val netCell = row.getOrNull(mapping.netColumn).orEmpty().trim()
            val grossCell = mapping.grossColumn?.let { row.getOrNull(it) }.orEmpty().trim()
            if (dateCell.isEmpty() && netCell.isEmpty()) continue
            if (isTotalLabel(dateCell)) continue
            val date = parseDate(dateCell, format)
            if (date == null) { issues += SalesRowIssue(number, SalesRowProblem.DATE, dateCell); continue }
            val net = parseAmount(netCell, format)
            if (net == null) { issues += SalesRowIssue(number, SalesRowProblem.NET_AMOUNT, netCell); continue }
            val gross = if (grossCell.isEmpty()) null else parseAmount(grossCell, format)
            if (grossCell.isNotEmpty() && gross == null) { issues += SalesRowIssue(number, SalesRowProblem.GROSS_AMOUNT, grossCell); continue }
            if (net < 0 || (gross ?: 0.0) < 0) { issues += SalesRowIssue(number, SalesRowProblem.NEGATIVE, netCell); continue }
            val before = byDate[date]
            byDate[date] = if (before == null) {
                SalesDay(date, net, gross, listOf(number))
            } else {
                SalesDay(date, before.net + net, sumOrNull(before.gross, gross), before.rows + number)
            }
        }
        if (byDate.isEmpty()) throw SalesFileException(SalesFileProblem.NO_SALES)
        return SalesSheet(format, mapping, byDate.values.sortedBy { it.date }.map { it.copy(net = it.net.roundCents(), gross = it.gross?.roundCents()) }, issues)
    }

    private fun sumOrNull(a: Double?, b: Double?): Double? = if (a == null && b == null) null else (a ?: 0.0) + (b ?: 0.0)

    private fun Double.roundCents(): Double = kotlin.math.round(this * 100.0) / 100.0

    // --- Columns --------------------------------------------------------------------------

    private val DATE_NAMES = setOf(
        "data", "date", "day", "dzien", "datasprzedazy", "dataraportu", "datadokumentu", "dzienhandlowy",
        "salesdate", "businessdate", "reportdate"
    )
    private val TOTAL_LABELS = listOf("suma", "razem", "total", "sum", "ogolem")

    private enum class Kind { DATE, NET, GROSS }

    private fun kindOf(header: String): Kind? {
        val name = normalise(header)
        if (name.isEmpty()) return null
        return when {
            name in DATE_NAMES || name.startsWith("data") && !name.contains("netto") && !name.contains("brutto") -> Kind.DATE
            name.contains("brutto") || name.contains("gross") -> Kind.GROSS
            name.contains("netto") || name.startsWith("net") || name.endsWith("net") || name == "sprzedaz" || name == "utarg" ||
                name == "obrot" || name == "sales" || name == "netsales" -> Kind.NET
            else -> null
        }
    }

    internal fun mappingOf(rows: List<List<String>>, format: SalesFileFormat): SalesColumnMapping {
        for (index in 0 until minOf(HEADER_SEARCH_ROWS, rows.size)) {
            val row = rows[index]
            val kinds = row.map(::kindOf)
            // Of several net (gross) columns, the total ("Netto razem") is the day's.
            fun pick(kind: Kind): Int? {
                val candidates = kinds.indices.filter { kinds[it] == kind }
                return candidates.firstOrNull { column -> TOTAL_LABELS.any { normalise(row[column]).contains(it) } } ?: candidates.firstOrNull()
            }
            val date = pick(Kind.DATE) ?: -1
            val net = pick(Kind.NET) ?: -1
            if (date >= 0 && net >= 0) {
                val gross = pick(Kind.GROSS)
                return SalesColumnMapping(index, date, net, gross, row[date].trim(), row[net].trim(), gross?.let { row[it].trim() })
            }
        }
        // No names: date, net, gross by position. A first row that is not data counts as a header.
        val first = rows.indexOfFirst { row -> row.any { it.isNotBlank() } }
        val firstRow = rows[first]
        val headerRow = if (parseDate(firstRow.getOrNull(0).orEmpty().trim(), format) == null) first else null
        val width = rows.maxOf { it.size }
        return SalesColumnMapping(
            headerRow = headerRow,
            dateColumn = 0,
            netColumn = 1,
            grossColumn = if (width >= 3) 2 else null,
            dateHeader = headerRow?.let { firstRow.getOrNull(0)?.trim() },
            netHeader = headerRow?.let { firstRow.getOrNull(1)?.trim() },
            grossHeader = headerRow?.let { firstRow.getOrNull(2)?.trim() }
        )
    }

    private fun isTotalLabel(cell: String): Boolean {
        val name = normalise(cell)
        return name.isNotEmpty() && TOTAL_LABELS.any { name.startsWith(it) }
    }

    /** Lower case, Polish letters without accents, letters and digits only. */
    internal fun normalise(text: String): String = buildString {
        for (c in text.lowercase()) {
            val plain = when (c) {
                'ą' -> 'a'; 'ć' -> 'c'; 'ę' -> 'e'; 'ł' -> 'l'; 'ń' -> 'n'; 'ó' -> 'o'; 'ś' -> 's'; 'ź', 'ż' -> 'z'
                else -> c
            }
            if (plain in 'a'..'z' || plain in '0'..'9') append(plain)
        }
    }

    // --- Values ---------------------------------------------------------------------------

    private val EXCEL_EPOCH = LocalDate(1899, 12, 30)

    /** A date as exports write it (see the class); null when the cell is none. */
    fun parseDate(cell: String, format: SalesFileFormat = SalesFileFormat.CSV): LocalDate? {
        val text = cell.trim().substringBefore('T').substringBefore(' ')
        if (text.isEmpty()) return null
        excelSerial(text)?.let { return it }
        val parts = text.split('-', '.', '/').filter { it.isNotEmpty() }
        if (parts.size != 3 || parts.any { part -> part.any { !it.isDigit() } }) return null
        val numbers = parts.map { it.toInt() }
        val (year, month, day) = when {
            parts[0].length == 4 -> Triple(numbers[0], numbers[1], numbers[2])
            parts[2].length == 4 -> Triple(numbers[2], numbers[1], numbers[0])
            else -> return null
        }
        if (year < 2000 || year > 2100) return null
        return runCatching { LocalDate(year, month, day) }.getOrNull()
    }

    /** Excel's day number (45658 = 2025-01-01), whole or with a time fraction. */
    private fun excelSerial(text: String): LocalDate? {
        val whole = text.substringBefore('.')
        if (whole.length != 5 || whole.any { !it.isDigit() }) return null
        val fraction = text.substringAfter('.', "")
        if (fraction.any { !it.isDigit() }) return null
        val days = whole.toInt()
        if (days < 36526 || days > 73050) return null // 2000-01-01 .. 2099-12-31
        return EXCEL_EPOCH.plus(DatePeriod(days = days))
    }

    /**
     * An amount as written: spaces (also non-breaking), currency and letters are ignored; with both
     * `,` and `.` the last one is the decimal point; a single `,` or `.` followed by exactly three
     * digits groups thousands, otherwise it is the decimal point. An XLSX number cell ("1234.5")
     * is read as is.
     */
    fun parseAmount(cell: String, format: SalesFileFormat = SalesFileFormat.CSV): Double? {
        val raw = cell.trim()
        if (raw.isEmpty()) return null
        if (format == SalesFileFormat.XLSX && isPlainNumber(raw)) return raw.toDoubleOrNull()?.takeIf { it.isFinite() }
        val negative = raw.startsWith("-") || (raw.startsWith("(") && raw.endsWith(")"))
        val kept = raw.filter { it.isDigit() || it == ',' || it == '.' }
        if (kept.none { it.isDigit() }) return null
        if (raw.any { it.isLetter() } && raw.filter { it.isLetter() }.lowercase() !in CURRENCY_WORDS) return null
        val lastComma = kept.lastIndexOf(',')
        val lastDot = kept.lastIndexOf('.')
        val normal = when {
            lastComma >= 0 && lastDot >= 0 -> {
                val decimal = if (lastComma > lastDot) ',' else '.'
                val grouping = if (decimal == ',') '.' else ','
                kept.filter { it != grouping }.replace(decimal, '.')
            }
            lastComma >= 0 || lastDot >= 0 -> {
                val mark = if (lastComma >= 0) ',' else '.'
                val count = kept.count { it == mark }
                val digitsAfter = kept.length - kept.lastIndexOf(mark) - 1
                if (count > 1 || digitsAfter == 3) kept.filter { it != mark } else kept.replace(mark, '.')
            }
            else -> kept
        }
        val value = normal.toDoubleOrNull() ?: return null
        return if (negative) -value else value
    }

    private val CURRENCY_WORDS = setOf("zł", "zl", "pln", "eur", "usd", "gbp", "czk", "kč")

    private fun isPlainNumber(text: String): Boolean {
        val body = text.removePrefix("-")
        val mantissa = body.substringBefore('E').substringBefore('e')
        val exponent = body.substring(mantissa.length).drop(1).removePrefix("-").removePrefix("+")
        return mantissa.isNotEmpty() && mantissa.first().isDigit() && mantissa.count { it == '.' } <= 1 &&
            mantissa.all { it.isDigit() || it == '.' } && exponent.all { it.isDigit() } &&
            (mantissa.length == body.length || exponent.isNotEmpty())
    }
}
